import { formatters, TextBox } from '@app/ui';
import { useGetCatalogCareHomeQuery } from '@app/medical-record/api';
import { useUserContextSelector } from '@app/main/utils';
import {
  AppointmentRequestDeclineBlurb,
  AppointmentRequestDetailsWidgetProps,
  useGetAppointmentRequestQuery,
} from '@mod/schedule';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';

export function CustomAppointmentRequestDetailsWidget(props: AppointmentRequestDetailsWidgetProps) {
  const { id, readonly } = props;
  const { data } = useGetAppointmentRequestQuery({ id });
  const { t } = useTranslation(undefined, { keyPrefix: 'appointments' });
  const [isCareHomeStaff, userCareHomeId] = useUserContextSelector((x) => [
    x.hasRole('CARE_HOME_STAFF'),
    x.careHomeId,
  ]);
  const isGroup = (data?.visitorRefs.length ?? 0) > 1;
  const firstVisitor = data?.visitorRefs[0]?.visitor ?? null;
  const careHomeId = isCareHomeStaff ? userCareHomeId : undefined;
  const { data: careHome } = useGetCatalogCareHomeQuery(
    { id: careHomeId! },
    { skip: !readonly || !isGroup || !careHomeId },
  );
  const groupCareHome = isCareHomeStaff ? careHome : firstVisitor?.careHome;
  const getAppointmentTypeName = (appointmentType?: { name?: string } | null) =>
    appointmentType?.name ?? t('notApplicable');

  if (!data) {
    return null;
  }

  return (
    <>
      {isGroup && (
        <>
          <TextBox label={<span className="text-description">{t('careHome')}</span>}>
            {groupCareHome?.name ?? t('notApplicable')}
          </TextBox>

          <TextBox label={<span className="text-description">{t('address')}</span>}>
            {groupCareHome?.address ? formatters.address(groupCareHome.address) : t('notApplicable')}
          </TextBox>
        </>
      )}

      {isGroup ? (
        <Row gutter={24}>
          <Col span={12}>
            <TextBox label={<span className="text-description">{t('visitor_plural')}</span>}>
              {data.visitorRefs.map((visitorRef) => (
                <div key={visitorRef.visitor.id}>{formatters.name(visitorRef.visitor)}</div>
              ))}
            </TextBox>
          </Col>
          <Col span={12}>
            <TextBox label={<span className="text-description">{t('reference')}</span>}>
              {data.visitorRefs.map((visitorRef) => (
                <div key={visitorRef.visitor.id}>{getAppointmentTypeName(visitorRef.appointmentType)}</div>
              ))}
            </TextBox>
          </Col>
        </Row>
      ) : (
        <>
          <TextBox label={<span className="text-description">{t('reference')}</span>}>
            {getAppointmentTypeName(data.visitorRefs[0].appointmentType)}
          </TextBox>

          <TextBox label={<span className="text-description">{t('visitor')}</span>}>
            {formatters.name(data.visitorRefs[0].visitor)}
          </TextBox>

          <TextBox label={<span className="text-description">{t('address')}</span>}>
            {data.visitorRefs[0].visitor.address
              ? formatters.address(data.visitorRefs[0].visitor.address)
              : t('notApplicable')}
          </TextBox>
        </>
      )}

      {data.requestedPeriod && (
        <TextBox label={<span className="text-description">{t('requests.requestedPeriod')}</span>}>
          {`${formatters.datetime(data.requestedPeriod.start)} - ${formatters.timeOfTheDay(
            data.requestedPeriod.end,
          )}`}
        </TextBox>
      )}

      <TextBox label={<span className="text-description">{t('requests.note')}</span>}>
        {data.note ?? t('notApplicable')}
      </TextBox>

      <AppointmentRequestDeclineBlurb declinedNote={data.declinedNote} />
    </>
  );
}
